import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Responsive,
  useContainerWidth,
  verticalCompactor,
} from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { useWidgetGridContext } from './context';
import { useWidgetGrid } from './hooks';
import { WidgetCardShell, WidgetErrorState, WidgetEmptyState } from './components';
import WidgetWrapper from './components/WidgetWrapper';
import './WidgetGrid.css';
import { BREAKPOINTS, BREAKPOINT_COLS, DRAG_HANDLE_OFFSET_PX, GRID_MARGIN, WIDGET_GRID_COPY } from './constants';
import { getBreakpointFromWidth, getWidgetLayoutWidth } from './utils';

const WidgetGrid = ({
  dashboardUuid = null,
  externalWidgetConfig = null,
  onExternalLayoutChange = null,
  onDashboardRetry = () => window.location.reload(),
  renderWidget = null,
  readOnly = false,
  className = '',
}) => {
  const { userId, isEditMode, dataAdapters } = useWidgetGridContext();
  const { width, containerRef, mounted } = useContainerWidth();

  const isExternalMode = Boolean(externalWidgetConfig);

  const internalGrid = useWidgetGrid({
    dashboardUuid: isExternalMode ? null : dashboardUuid,
    userId,
    fetchDashboardLayout: dataAdapters.fetchDashboardLayout,
  });

  const widgetConfig = isExternalMode ? externalWidgetConfig : internalGrid.widgetConfig;
  const configLoading = isExternalMode ? false : internalGrid.configLoading;
  const configError = isExternalMode ? null : internalGrid.configError;
  // `?? undefined`, not the raw prop: react-grid-layout defaults
  // `onLayoutChange` to a noop, and React only applies a default for
  // `undefined` — an explicit `null` sails past it and rgl then calls null on
  // mount. A read-only grid (the dashboard detail page) passes no handler at
  // all, so that was every such grid throwing and blanking the page.
  const handleLayoutChange =
    (isExternalMode ? onExternalLayoutChange : internalGrid.handleLayoutChange) ?? undefined;

  const rowHeight = widgetConfig?.gridConfig?.rowHeight ?? 24;
  const margin = widgetConfig?.gridConfig?.margin ?? GRID_MARGIN;

  // `cols` overrides how many columns each breakpoint has.
  //
  // The defaults key column count to the CONTAINER's width, which is right for
  // a dashboard — the grid is the page — and wrong for a canvas that previews
  // one. A widget builder's preview column is ~530px, which lands on `xs` and
  // collapses a twelve-column dashboard to four, so a widget the author sized
  // to a third of the page draws at a third of four columns. A preview says
  // what the layout will be, not what its own box can fit, so it passes the
  // dashboard's column count in and ignores its own width.
  //
  // It has to be an object keyed by breakpoint. `Responsive` indexes it by the
  // active breakpoint name and throws — taking the whole page down with it,
  // since nothing above catches — when the entry is missing. A caller passing
  // a bare number (`cols: 12`) is the shape that did exactly that: the value
  // was inert until this prop started reading it, and then every dashboard
  // with a widget in it rendered blank. Ignoring a malformed value is the only
  // behaviour that cannot regress a caller.
  const cols = useMemo(() => {
    const requested = widgetConfig?.gridConfig?.cols;
    if (!requested || typeof requested !== 'object') return BREAKPOINT_COLS;
    const missing = Object.keys(BREAKPOINT_COLS).filter((bp) => requested[bp] == null);
    if (missing.length > 0) {
      // Partial is as fatal as absent once the viewport reaches that
      // breakpoint, and a grid that breaks only on a small screen is worse
      // than one that ignores the override.
      console.warn(
        `[WidgetGrid] gridConfig.cols is missing ${missing.join(', ')}; using the defaults.`,
      );
      return BREAKPOINT_COLS;
    }
    return requested;
  }, [widgetConfig?.gridConfig?.cols]);
  const horizontalContainerPadding = isExternalMode ? 0 : margin[0] + DRAG_HANDLE_OFFSET_PX;
  const containerPadding = useMemo(
    () => [horizontalContainerPadding, margin[1]],
    [horizontalContainerPadding, margin],
  );

  const canInteract = isEditMode && !readOnly;

  const dragConfig = useMemo(
    () => ({
      enabled: canInteract,
      handle: '.widget-drag-handle',
    }),
    [canInteract],
  );

  const resizeConfig = useMemo(
    () => ({
      enabled: canInteract,
      handles: ['s', 'n', 'e', 'w'],
    }),
    [canInteract],
  );

  const layouts = useMemo(
    () => widgetConfig?.layouts ?? {},
    [widgetConfig?.layouts],
  );

  const breakpoint = useMemo(
    () => getBreakpointFromWidth(width, BREAKPOINTS),
    [width],
  );

  const children = useMemo(() => {
    const widgets = widgetConfig?.widgets ?? [];
    return widgets.map((w) => {
      const layoutW = getWidgetLayoutWidth(
        w.instance_uuid,
        layouts,
        breakpoint,
        w.layout?.w,
      );
      return (
        <div key={w.instance_uuid} className="w-full h-full min-h-0">
          {renderWidget
            ? renderWidget(w, layoutW)
            : <WidgetWrapper widgetConfig={w} layoutW={layoutW} />}
        </div>
      );
    });
  }, [widgetConfig?.widgets, layouts, breakpoint, renderWidget]);

  const renderContent = () => {
    if (configLoading) {
      return (
        <WidgetCardShell>
          <WidgetEmptyState message={WIDGET_GRID_COPY.states.loadingDashboard} />
        </WidgetCardShell>
      );
    }
    if (configError) {
      return (
        <WidgetCardShell>
          <WidgetErrorState
            message={configError.message || WIDGET_GRID_COPY.errors.dashboardLoad}
            onRetry={onDashboardRetry}
          />
        </WidgetCardShell>
      );
    }
    if (!widgetConfig?.widgets?.length) {
      return (
        <WidgetCardShell>
          <WidgetEmptyState message={WIDGET_GRID_COPY.states.noWidgets} />
        </WidgetCardShell>
      );
    }
    return (
      mounted && (
        <Responsive
          width={width}
          breakpoints={BREAKPOINTS}
          cols={cols}
          layouts={layouts}
          rowHeight={rowHeight}
          margin={margin}
          containerPadding={containerPadding}
          dragConfig={dragConfig}
          resizeConfig={resizeConfig}
          compactor={verticalCompactor}
          onLayoutChange={handleLayoutChange}
          autoSize
        >
          {children}
        </Responsive>
      )
    );
  };

  return (
    <div
      ref={containerRef}
      className={['wg-container w-full min-h-[200px] overflow-x-visible', className]
        .filter(Boolean)
        .join(' ')}
    >
      {renderContent()}
    </div>
  );
};

WidgetGrid.propTypes = {
  dashboardUuid: PropTypes.string,
  externalWidgetConfig: PropTypes.shape({
    widgets: PropTypes.array,
    layouts: PropTypes.object,
    /** `{ rowHeight?, margin?, cols? }` — `cols` overrides the per-breakpoint column count. */
    gridConfig: PropTypes.object,
  }),
  onExternalLayoutChange: PropTypes.func,
  onDashboardRetry: PropTypes.func,
  renderWidget: PropTypes.func,
  readOnly: PropTypes.bool,
  /**
   * Extra classes on the grid container. The 200px floor exists so an empty
   * dashboard is not a zero-height page; a one-widget canvas that hugs its
   * content undoes it with `!min-h-0`.
   */
  className: PropTypes.string,
};


export default WidgetGrid;
